// GENERATED by blackbird_backend/scripts/tools/generate-discipline-registry.mjs. Do not edit.
// Source: blackbird_backend/src/layers/shared-utils/nodejs/clinical/disciplines.js (Phase 20A)

export type DisciplineStatus = 'active' | 'planned';
export type ProviderRole = 'licensed' | 'assistant';
export type EvvClass = 'REQUIRED' | 'OPTIONAL' | 'NOT_REQUIRED';

export interface DisciplineEntry {
  code: string;
  label: string;
  longLabel: string;
  referralLabel: string;
  status: DisciplineStatus;
  kind: 'billable' | 'documented';
  color: string;
  optionalTrack: boolean;
  forms: { assessment: boolean; carePlan: boolean; cms485: boolean; formKeys: boolean };
  therapy: boolean;
  /** Phase 20C: GP / GO / GN on therapy claim lines */
  therapyModifier?: string;
  skilled: boolean;
  /** Phase 20C: may supervise a home health aide */
  supervisesAides?: boolean;
  /** Phase 20D: visits are charted with the AIDE_TASKS note */
  aideTasks?: boolean;
  /** Phase 20E: the discipline's visit note type (noteTemplates.ts) */
  noteType?: string;
  /** Phase 20E: days between care plan reviews */
  planReviewDays?: number;
  /** Phase 20B: clinical template ids (clinicalTemplates.ts) for disciplines without a hand-built form */
  templates?: { assessment?: string; carePlan?: string };
}

export interface ProviderTypeEntry {
  code: string;
  label: string;
  discipline: string;
  role: ProviderRole;
  status: DisciplineStatus;
  limitedNursing?: boolean;
}

export interface PaCategoryEntry {
  code: string;
  shortLabel: string;
  discipline: string;
  service: string;
  label: string;
  unitType: 'units' | 'visits' | 'hours';
  minutesPerUnit: number | null;
  showServiceType: boolean;
  flowsheetGated: boolean;
  uaAllowed: boolean;
  status: DisciplineStatus;
}

/** Phase 20D: attendant programme and its T1019 modifier */
export interface AttendantProgramEntry {
  code: string;
  label: string;
  modifier: string;
}

export interface ServiceEntry {
  code: string;
  label: string;
  discipline: string;
  evv: EvvClass;
  status: DisciplineStatus;
}

export const DISCIPLINE_ENTRIES = [
  {
    "code": "RN",
    "label": "Nursing",
    "longLabel": "Private Duty Nursing",
    "referralLabel": "RN (Private Duty Nursing)",
    "status": "active",
    "kind": "billable",
    "color": "sky",
    "optionalTrack": false,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": true
    },
    "therapy": false,
    "supervisesAides": true,
    "skilled": true
  },
  {
    "code": "PT",
    "label": "Physical Therapy",
    "longLabel": "Physical Therapy",
    "referralLabel": "PT (Physical Therapy)",
    "status": "active",
    "kind": "billable",
    "color": "emerald",
    "optionalTrack": false,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": true
    },
    "therapy": true,
    "therapyModifier": "GP",
    "supervisesAides": true,
    "skilled": true
  },
  {
    "code": "OT",
    "label": "Occupational Therapy",
    "longLabel": "Occupational Therapy",
    "referralLabel": "OT (Occupational Therapy)",
    "status": "active",
    "kind": "billable",
    "color": "violet",
    "optionalTrack": false,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": true
    },
    "therapy": true,
    "therapyModifier": "GO",
    "supervisesAides": true,
    "skilled": true
  },
  {
    "code": "ST",
    "label": "Speech Therapy",
    "longLabel": "Speech-Language Pathology",
    "referralLabel": "ST (Speech Therapy)",
    "status": "active",
    "kind": "billable",
    "color": "amber",
    "optionalTrack": false,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": true
    },
    "therapy": true,
    "therapyModifier": "GN",
    "skilled": true
  },
  {
    "code": "MSW",
    "label": "Medical Social Work",
    "longLabel": "Medical Social Services",
    "referralLabel": "MSW (Medical Social Services)",
    "status": "active",
    "kind": "documented",
    "color": "rose",
    "optionalTrack": true,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": false
    },
    "templates": {
      "assessment": "MSW_PSYCHOSOCIAL",
      "carePlan": "MSW_CARE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "noteType": "PSYCHOSOCIAL_NOTE",
    "planReviewDays": 60
  },
  {
    "code": "HHA",
    "label": "Home Health Aide",
    "longLabel": "Home Health Aide",
    "referralLabel": "HHA (Home Health Aide)",
    "status": "active",
    "kind": "billable",
    "color": "lime",
    "optionalTrack": true,
    "forms": {
      "assessment": false,
      "carePlan": true,
      "cms485": true,
      "formKeys": false
    },
    "templates": {
      "carePlan": "HHA_AIDE_CARE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "aideTasks": true
  },
  {
    "code": "ATTENDANT",
    "label": "Personal Care / CFC",
    "longLabel": "Personal Care and CFC Attendant Services",
    "referralLabel": "Personal care / CFC attendant",
    "status": "active",
    "kind": "billable",
    "color": "teal",
    "optionalTrack": true,
    "forms": {
      "assessment": false,
      "carePlan": true,
      "cms485": false,
      "formKeys": false
    },
    "templates": {
      "carePlan": "ATTENDANT_SERVICE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "aideTasks": true
  },
  {
    "code": "RT",
    "label": "Respiratory Therapy",
    "longLabel": "Respiratory Therapy",
    "referralLabel": "RT (Respiratory Therapy)",
    "status": "active",
    "kind": "billable",
    "color": "cyan",
    "optionalTrack": true,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": false
    },
    "templates": {
      "assessment": "RT_ASSESSMENT",
      "carePlan": "RT_CARE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "noteType": "RT_TREATMENT"
  },
  {
    "code": "RD",
    "label": "Nutrition",
    "longLabel": "Nutrition (Registered Dietitian)",
    "referralLabel": "RD (Nutrition)",
    "status": "active",
    "kind": "documented",
    "color": "orange",
    "optionalTrack": true,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": true,
      "formKeys": false
    },
    "templates": {
      "assessment": "RD_NUTRITION_ASSESSMENT",
      "carePlan": "RD_CARE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "noteType": "NUTRITION_NOTE",
    "planReviewDays": 60
  },
  {
    "code": "BH",
    "label": "Behavioral Health",
    "longLabel": "Behavioral Health",
    "referralLabel": "Behavioral health",
    "status": "active",
    "kind": "documented",
    "color": "fuchsia",
    "optionalTrack": true,
    "forms": {
      "assessment": true,
      "carePlan": true,
      "cms485": false,
      "formKeys": false
    },
    "templates": {
      "assessment": "BH_ASSESSMENT",
      "carePlan": "BH_CARE_PLAN"
    },
    "therapy": false,
    "skilled": false,
    "noteType": "BH_SESSION",
    "planReviewDays": 60
  }
] as const;

export const PROVIDER_TYPE_ENTRIES = [
  {
    "code": "RN",
    "label": "Registered Nurse",
    "discipline": "RN",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "LVN",
    "label": "Licensed Vocational Nurse",
    "discipline": "RN",
    "role": "licensed",
    "status": "active",
    "limitedNursing": true
  },
  {
    "code": "PT",
    "label": "Physical Therapist",
    "discipline": "PT",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "PTA",
    "label": "Physical Therapist Assistant",
    "discipline": "PT",
    "role": "assistant",
    "status": "active"
  },
  {
    "code": "OT",
    "label": "Occupational Therapist",
    "discipline": "OT",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "COTA",
    "label": "Certified Occupational Therapy Assistant",
    "discipline": "OT",
    "role": "assistant",
    "status": "active"
  },
  {
    "code": "ST",
    "label": "Speech-Language Pathologist",
    "discipline": "ST",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "SLPA",
    "label": "Speech-Language Pathology Assistant",
    "discipline": "ST",
    "role": "assistant",
    "status": "active"
  },
  {
    "code": "MSW",
    "label": "Medical Social Worker",
    "discipline": "MSW",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "HHA",
    "label": "Home Health Aide",
    "discipline": "HHA",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "ATTENDANT",
    "label": "Personal Attendant",
    "discipline": "ATTENDANT",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "RT",
    "label": "Respiratory Therapist",
    "discipline": "RT",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "RD",
    "label": "Registered Dietitian",
    "discipline": "RD",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "LPC",
    "label": "Licensed Professional Counselor",
    "discipline": "BH",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "LCSW",
    "label": "Licensed Clinical Social Worker",
    "discipline": "BH",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "BCBA",
    "label": "Board Certified Behavior Analyst",
    "discipline": "BH",
    "role": "licensed",
    "status": "active"
  },
  {
    "code": "RBT",
    "label": "Registered Behavior Technician",
    "discipline": "BH",
    "role": "assistant",
    "status": "active"
  }
] as const;

export const PA_CATEGORY_ENTRIES = [
  {
    "code": "pdn",
    "shortLabel": "PDN",
    "discipline": "RN",
    "service": "PDN",
    "label": "Private Duty Nursing (PDN)",
    "unitType": "units",
    "minutesPerUnit": 15,
    "showServiceType": true,
    "flowsheetGated": true,
    "uaAllowed": true,
    "status": "active"
  },
  {
    "code": "sn_visit",
    "shortLabel": "SN visit",
    "discipline": "RN",
    "service": "SKILLED_NURSING_VISIT",
    "label": "Skilled Nursing Visit",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "pt",
    "shortLabel": "PT",
    "discipline": "PT",
    "service": "PT",
    "label": "Physical Therapy (PT)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "ot",
    "shortLabel": "OT",
    "discipline": "OT",
    "service": "OT",
    "label": "Occupational Therapy (OT)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "st",
    "shortLabel": "ST",
    "discipline": "ST",
    "service": "ST",
    "label": "Speech Therapy (ST)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "hha",
    "shortLabel": "HHA",
    "discipline": "HHA",
    "service": "HHA",
    "label": "Home Health Aide (HHA)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "msw",
    "shortLabel": "MSW",
    "discipline": "MSW",
    "service": "MSW",
    "label": "Medical Social Work (MSW)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "attendant",
    "shortLabel": "PCS/CFC",
    "discipline": "ATTENDANT",
    "service": "PCS",
    "label": "Personal Care / CFC Attendant",
    "unitType": "units",
    "minutesPerUnit": 15,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "rt",
    "shortLabel": "RT",
    "discipline": "RT",
    "service": "RT",
    "label": "Respiratory Therapy (RT)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "rd",
    "shortLabel": "RD",
    "discipline": "RD",
    "service": "RD",
    "label": "Nutrition (RD)",
    "unitType": "visits",
    "minutesPerUnit": null,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  },
  {
    "code": "bh",
    "shortLabel": "BH",
    "discipline": "BH",
    "service": "BH",
    "label": "Behavioral Health",
    "unitType": "units",
    "minutesPerUnit": 15,
    "showServiceType": false,
    "flowsheetGated": false,
    "uaAllowed": false,
    "status": "active"
  }
] as const;

export const SERVICE_ENTRIES = [
  {
    "code": "PDN",
    "label": "PDN",
    "discipline": "RN",
    "evv": "OPTIONAL",
    "status": "active"
  },
  {
    "code": "SKILLED_NURSING_VISIT",
    "label": "Skilled nursing",
    "discipline": "RN",
    "evv": "REQUIRED",
    "status": "active"
  },
  {
    "code": "PT",
    "label": "PT",
    "discipline": "PT",
    "evv": "REQUIRED",
    "status": "active"
  },
  {
    "code": "OT",
    "label": "OT",
    "discipline": "OT",
    "evv": "REQUIRED",
    "status": "active"
  },
  {
    "code": "ST",
    "label": "ST",
    "discipline": "ST",
    "evv": "NOT_REQUIRED",
    "status": "active"
  },
  {
    "code": "HHA",
    "label": "Aide",
    "discipline": "HHA",
    "evv": "REQUIRED",
    "status": "active"
  },
  {
    "code": "MSW",
    "label": "MSW",
    "discipline": "MSW",
    "evv": "NOT_REQUIRED",
    "status": "active"
  },
  {
    "code": "PCS",
    "label": "Personal care",
    "discipline": "ATTENDANT",
    "evv": "REQUIRED",
    "status": "active"
  },
  {
    "code": "RT",
    "label": "RT",
    "discipline": "RT",
    "evv": "NOT_REQUIRED",
    "status": "active"
  },
  {
    "code": "RD",
    "label": "Nutrition",
    "discipline": "RD",
    "evv": "NOT_REQUIRED",
    "status": "active"
  },
  {
    "code": "BH",
    "label": "Behavioral health",
    "discipline": "BH",
    "evv": "NOT_REQUIRED",
    "status": "active"
  }
] as const;

export const EVALUATION_VISIT_TYPES = [
  "INITIAL",
  "EVALUATION",
  "REEVALUATION"
] as const;

export const ATTENDANT_PROGRAM_ENTRIES: readonly AttendantProgramEntry[] = [
  {
    "code": "PCS",
    "label": "Personal care services (PCS)",
    "modifier": "U6"
  },
  {
    "code": "CFC_PAS",
    "label": "CFC personal assistance services",
    "modifier": "UD"
  },
  {
    "code": "CFC_HAB",
    "label": "CFC personal assistance and habilitation",
    "modifier": "U9"
  }
];

/** Every discipline code the registry knows (active and planned). */
export type DisciplineCode = (typeof DISCIPLINE_ENTRIES)[number]['code'];
/** Every provider type code (active and planned). */
export type ProviderTypeCode = (typeof PROVIDER_TYPE_ENTRIES)[number]['code'];
/** Every PA service category code. */
export type PaCategoryCode = (typeof PA_CATEGORY_ENTRIES)[number]['code'];
/** Every visit service code (billing rows, claim rules). */
export type ServiceCode = (typeof SERVICE_ENTRIES)[number]['code'];

// Compile-time shape checks (no satisfies: the CRA Babel preset predates it)
const _disciplines: readonly DisciplineEntry[] = DISCIPLINE_ENTRIES;
const _providerTypes: readonly ProviderTypeEntry[] = PROVIDER_TYPE_ENTRIES;
const _paCategories: readonly PaCategoryEntry[] = PA_CATEGORY_ENTRIES;
const _services: readonly ServiceEntry[] = SERVICE_ENTRIES;
export const REGISTRY_SHAPE_CHECK = [_disciplines, _providerTypes, _paCategories, _services].length;
